import { trackEvent } from "@/lib/analytics";
import { CTA_LABELS, MEASURED_PAGES } from "@/lib/cta-measurement-shared";

export { CTA_LABELS, MEASURED_PAGES };

type Measurement = "exposure" | "click" | "appointment_request" | "contact_request" | "phone_tap";
type Source = { page: (typeof MEASURED_PAGES)[number]; label: string | null };
const SESSION_KEY = "vb_cta_measurement_session";
const SOURCE_KEY = "vb_cta_measurement_source";
const SEEN_KEY = "vb_cta_measurement_seen";

function isPage(path: string): path is Source["page"] {
  return Object.hasOwn(CTA_LABELS, path);
}

function session(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function source(): Source | null {
  try {
    const raw = sessionStorage.getItem(SOURCE_KEY);
    const value = raw ? JSON.parse(raw) as Source : null;
    return value && isPage(value.page) && (value.label === null || CTA_LABELS[value.page].includes(value.label)) ? value : null;
  } catch {
    return null;
  }
}

export function measurePageVisit(path: string) {
  if (!isPage(path) || source()) return;
  try { sessionStorage.setItem(SOURCE_KEY, JSON.stringify({ page: path, label: null })); } catch { /* storage unavailable */ }
}

function device(): "phone" | "tablet" | "desktop" {
  const width = window.innerWidth;
  return width < 640 ? "phone" : width < 1024 ? "tablet" : "desktop";
}

function send(eventType: Measurement, origin: Source) {
  const id = session();
  if (!id) return;
  const payload = JSON.stringify({ event_type: eventType, session_id: id, source_page: origin.page, cta_label: origin.label, device: device(), language: origin.page.startsWith("/es/") ? "es" : "en" });
  // Same-origin, anonymous aggregate measurement; no patient details or URL query strings.
  try {
    fetch("/api/public/cta-measurement", { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  } catch {
    // Measurement must never interrupt the visitor's action.
  }
  // SectionCta already reports its click to GA4 with destination information.
  if (eventType !== "click") {
    trackEvent(`in_page_cta_${eventType}`, { cta_label: origin.label ?? "none", source_page: origin.page, device_class: device(), language: origin.page.startsWith("/es/") ? "es" : "en" });
  }
}

export function measureExposure(label: string) {
  const page = window.location.pathname;
  if (!isPage(page) || !CTA_LABELS[page].includes(label)) return;
  try {
    const key = `${page}:${label}`;
    const seen = JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? "[]") as string[];
    if (seen.includes(key)) return;
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen, key]));
    send("exposure", { page, label });
  } catch {
    // Storage blocked: record a view anyway rather than undercounting every click.
    send("exposure", { page, label });
  }
}

export function measureClick(label: string) {
  const page = window.location.pathname;
  if (!isPage(page) || !CTA_LABELS[page].includes(label)) return;
  measureExposure(label);
  const origin = { page, label };
  try { sessionStorage.setItem(SOURCE_KEY, JSON.stringify(origin)); } catch { /* storage unavailable */ }
  send("click", origin);
}

export function measureConversion(eventType: "appointment_request" | "contact_request" | "phone_tap") {
  if (typeof window === "undefined") return;
  const previous = source();
  const page = window.location.pathname;
  const origin = previous ?? (isPage(page) ? { page, label: null } : null);
  if (origin) send(eventType, origin);
}